import { cn } from '~/lib/cn';

interface FiberTextureProps {
  className?: string;
}

/**
 * Abstract composite-fibre bundle SVG — hero background decoration.
 * Represents cross-sectional fibre strands: diagonal wavy lines in cyan/electric-blue gradient.
 * Low opacity (~20%), scalable via viewBox, purely decorative.
 */
function FiberTexture({ className }: FiberTextureProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 800 600"
      aria-hidden="true"
      preserveAspectRatio="xMidYMid slice"
      className={cn('pointer-events-none select-none', className)}
    >
      <defs>
        {/* Gradient: cyan-signal → electric-blue */}
        <linearGradient id="fiber-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00C8FF" stopOpacity="0.18" />
          <stop offset="50%" stopColor="#0062FF" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#00C8FF" stopOpacity="0.06" />
        </linearGradient>

        {/* Radial fade mask so fibres vanish at edges */}
        <radialGradient id="fiber-mask" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="white" stopOpacity="1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </radialGradient>

        <mask id="fiber-fade">
          <rect width="800" height="600" fill="url(#fiber-mask)" />
        </mask>
      </defs>

      {/* Fibre strand group — masked with radial fade */}
      <g
        mask="url(#fiber-fade)"
        stroke="url(#fiber-gradient)"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      >
        {/* Generate diagonal wavy strands at ~20% density */}
        {/* Each strand is a cubic bezier — diagonal direction, slight wave */}
        <path d="M-40,100 C80,80 160,160 300,120 S520,60 660,100 S780,140 840,120" opacity="0.9" />
        <path
          d="M-40,160 C60,140 180,220 320,180 S540,120 680,160 S780,200 840,180"
          opacity="0.7"
        />
        <path
          d="M-40,220 C100,200 200,280 340,240 S560,180 700,220 S800,260 840,240"
          opacity="0.8"
        />
        <path
          d="M-40,280 C80,260 220,340 360,300 S580,240 720,280 S810,320 840,300"
          opacity="0.6"
        />
        <path
          d="M-40,340 C60,320 200,400 380,360 S600,300 740,340 S820,380 840,360"
          opacity="0.75"
        />
        <path
          d="M-40,400 C100,380 240,460 400,420 S620,360 760,400 S830,440 840,420"
          opacity="0.65"
        />
        <path
          d="M-40,460 C80,440 220,520 420,480 S640,420 780,460 S835,500 840,480"
          opacity="0.55"
        />
        <path
          d="M-40,520 C120,500 260,580 440,540 S660,480 800,520 S840,560 840,540"
          opacity="0.45"
        />

        {/* Counter-diagonal strands for fibre-bundle depth effect */}
        <path d="M100,-40 C80,80 160,200 120,340 S60,520 100,660" opacity="0.5" strokeWidth="1" />
        <path d="M200,-40 C180,80 260,200 220,340 S160,520 200,660" opacity="0.4" strokeWidth="1" />
        <path
          d="M340,-40 C320,80 400,200 360,340 S300,520 340,660"
          opacity="0.45"
          strokeWidth="1"
        />
        <path d="M480,-40 C460,80 540,200 500,340 S440,520 480,660" opacity="0.4" strokeWidth="1" />
        <path
          d="M620,-40 C600,80 680,200 640,340 S580,520 620,660"
          opacity="0.35"
          strokeWidth="1"
        />
        <path d="M760,-40 C740,80 820,200 780,340 S720,520 760,660" opacity="0.3" strokeWidth="1" />
      </g>
    </svg>
  );
}

export { FiberTexture };
export type { FiberTextureProps };
export default FiberTexture;
